/**
 * Agent 共享常量 — 前端降级兜底数据
 *
 * ⚠️ 运行时数据源为 stores/agentRegistry.ts（从后端 Agent 目录 API 加载）。
 *    本文件仅在 API 不可用时作为降级兜底，修改时须与后端 agent_registry.py 保持同步。
 *
 * 项目中存在两套命名体系：
 *  1. Agent ID（运行时）：chatbi, cs, ops …  —— 用于 Router 路由、自主模式覆盖、会话标签
 *  2. Agent Category（元数据/模板）：data_analyst, ops_pilot …  —— 用于 Agent 目录、能力配置
 */

import type { AgentMeta } from '@/types/agent-types'

// ======================== Agent ID 体系（运行时） ========================

/** Agent 成熟度等级（L0 = 概念，L4/L5 = 生产就绪） */
export type MaturityLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L4' | 'L5'

/** 技能标签（用于侧栏 Agent 卡片展示） */
export interface SkillTag {
  icon: string
  text: string
}

/** Agent 注册项（在 AgentMeta 基础上扩展路由路径） */
export interface AgentRegistryItem extends AgentMeta {
  /** 前端路由路径（用于"开始对话"跳转） */
  routePath?: string
  /** 是否允许用户手动切换，缺省时由调用方决定默认策略 */
  enabled?: boolean
  /**
   * Agent 成熟度等级（L0–L5）
   * L4+ 代表生产就绪，展示给最终用户
   * L3 及以下标记为「即将上线」
   */
  maturityLevel?: MaturityLevel
  /** 岗位头衔（如"首席数据官"），用于侧栏卡片展示 */
  title?: string
  /** 技能标签列表，用于侧栏卡片技能展示 */
  skillTags?: SkillTag[]
  /** 侧栏分组 key，对应 SIDEBAR_GROUPS 中的 key */
  sidebarGroup?: string
  /**
   * 治理展示面（PR6 / Phase 4）：DB 主导模式下由后端 surface_type 填充。
   * business/skill/internal/fallback/deprecated。
   */
  surfaceType?: 'business' | 'skill' | 'internal' | 'fallback' | 'deprecated' | null
  /** 是否进入 Agent 目录（PR6 / Phase 4）：DB 主导模式下由后端 list_visible 填充。 */
  listVisible?: boolean | null
}

/** 侧栏 Agent 分组配置 */
export const SIDEBAR_GROUPS: Record<string, { title: string; order: number }> = {
  business: { title: '业务提效', order: 1 },
  customer: { title: '客户经营', order: 2 },
  content: { title: '内容增长', order: 3 },
  data: { title: '数据基建', order: 4 }
}

/**
 * 判断 Agent 当前是否允许被用户手动切换。
 *
 * 这里使用显式 `=== true`，避免运行时数据缺失 `enabled` 字段时被误判为可点击。
 */
export function isAgentSelectable(
  agent: Pick<AgentRegistryItem, 'enabled'> | null | undefined
): boolean {
  return agent?.enabled === true
}

/**
 * 根据 Agent ID 判断是否允许切换。
 *
 * 用于父组件做二次守卫，防止子组件误触发事件后仍然完成路由切换。
 */
export function canSelectAgentById(
  agentId: string | null | undefined,
  agents: readonly Pick<AgentRegistryItem, 'id' | 'enabled'>[]
): boolean {
  if (!agentId) {
    return false
  }
  return isAgentSelectable(agents.find(agent => agent.id === agentId))
}

/**
 * 全量已注册 Agent
 *
 * ⚠️ enabled 为降级兜底值（API 不可用时生效），运行时以 useAgentRegistryStore() 为准。
 *    维护时须与后端 config.py 的 *_AGENT_ENABLED 保持同步。
 * 顺序按后端 agent_registry.py 注册顺序排列。
 */
export const REGISTERED_AGENTS: readonly AgentRegistryItem[] = [
  // ── 通用入口（不对应具体后端 Agent，仅用于首页默认态） ──
  // 品牌数据真相源：branding.ts 的 COORDINATOR_AGENT 从此处派生
  {
    id: 'auto',
    label: '望舒',
    role: 'AI 数字员工总协调',
    shortLabel: '望',
    icon: '🌙',
    color: '#6366f1',
    avatarUrl: '/avatars/avatar-auto.png',
    category: 'utility',
    business: false,
    enabled: false,
    routePath: '/agents/workspace',
    description: '智能任务分发，自动匹配最合适的 Agent 专家'
  },
  // ── 数据分析 ── L5: 完整 LangGraph 28 节点，生产验证
  {
    id: 'chatbi',
    label: '数策',
    role: '数据分析专家',
    shortLabel: '数',
    icon: '📊',
    color: '#3370ff',
    avatarUrl: '/avatars/avatar-chatbi.png',
    category: 'data_analyst',
    business: true,
    enabled: true,
    maturityLevel: 'L5',
    routePath: '/agents/workspace',
    description: '数据查询·可视化·报告·指标·血缘·ETL 全链路数据分析',
    title: '首席数据官',
    skillTags: [
      { icon: 'bar-chart', text: '数据洞察' },
      { icon: 'arrow-rise', text: '趋势推演' },
      { icon: 'branch', text: '归因分析' },
      { icon: 'file', text: '分析报告' },
      { icon: 'link', text: '数据血缘' },
      { icon: 'tool', text: 'ETL 诊断' }
    ],
    sidebarGroup: 'business'
  },
  // ── 游戏运营 ──
  {
    id: 'ops_message',
    label: '青鸟',
    role: '运营消息专家',
    shortLabel: '消',
    icon: '📨',
    color: '#f59e0b',
    avatarUrl: '/avatars/avatar-ops_message.png',
    category: 'ops_pilot',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=ops_message',
    description: '消息推送·公告管理·邮件·维护通知，全渠道运营触达',
    title: '运营触达专家',
    skillTags: [
      { icon: 'apps', text: '消息推送' },
      { icon: 'notification', text: '全服公告' },
      { icon: 'layout', text: '通知模板' },
      { icon: 'edit', text: '公告管理' }
    ],
    sidebarGroup: 'business'
  },
  // ── 鸣镝 · 独立应用推送 Agent（已脱离青鸟）──
  {
    id: 'push_ops',
    label: '鸣镝',
    role: '应用推送专家',
    shortLabel: '推',
    icon: '🎯',
    color: '#f59e0b',
    avatarUrl: '/avatars/avatar-ops_message.png',
    category: 'ops_pilot',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=push_ops',
    description: '应用/小程序推送的创建·编辑·数据统计，精准触达海量终端',
    title: '应用推送专家',
    skillTags: [
      { icon: 'apps', text: '推送创建' },
      { icon: 'edit', text: '推送编辑' },
      { icon: 'notification', text: '推送统计' },
      { icon: 'layout', text: '多端推送' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'announcement',
    label: '鸿雁',
    role: '公告管理专家',
    shortLabel: '告',
    icon: '📢',
    color: '#ff7d00',
    avatarUrl: '/avatars/avatar-announcement.jpg',
    category: 'ops_pilot',
    business: false, // 收敛为 ops_message 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=announcement',
    description: '游戏公告的查询、创建、编辑、删除、撤回与发布管理',
    title: '公告管理专家',
    skillTags: [
      { icon: 'notification', text: '公告查询' },
      { icon: 'edit', text: '草稿编辑' },
      { icon: 'safe', text: '发布校验' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'channel_ops',
    label: '百川',
    role: '渠道运营专家',
    shortLabel: '渠',
    icon: '🌐',
    color: '#3b82f6',
    avatarUrl: '/avatars/avatar-channel_ops.png',
    category: 'ops_pilot',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=channel_ops',
    description: '评论回复·退款监控·ASO·渠道配置·官包校验·资源管理',
    title: '渠道运营专家',
    skillTags: [
      { icon: 'apps', text: '渠道分析' },
      { icon: 'star', text: 'ASO 优化' },
      { icon: 'unordered-list', text: '渠道管理' },
      { icon: 'credit-card', text: '支付配置' },
      { icon: 'link', text: '域名管理' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'channel_management',
    label: '渠析',
    role: '渠道管理专家',
    shortLabel: '渠',
    icon: '🧭',
    color: '#2563eb',
    avatarUrl: '/avatars/avatar-channel_ops.png',
    category: 'ops_pilot',
    business: false, // 收敛为 channel_ops 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=channel_management',
    description: '主渠道查询、渠道配置维护、官包校验与发布区域管理',
    title: '渠道管理专家',
    skillTags: [
      { icon: 'search', text: '渠道查询' },
      { icon: 'settings', text: '渠道配置' },
      { icon: 'safe', text: '官包校验' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'anomaly_monitor',
    label: '谛听',
    role: '异常监控专家',
    shortLabel: '监',
    icon: '🔔',
    color: '#ef4444',
    avatarUrl: '/avatars/avatar-anomaly_monitor.png',
    category: 'ops_pilot',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=anomaly_monitor',
    description: '异常用户检测、行为日志分析、策划验收辅助',
    title: '异常监控专家',
    skillTags: [
      { icon: 'exclamation-circle', text: '异常检测' },
      { icon: 'eye', text: '行为分析' },
      { icon: 'thunderbolt', text: '告警配置' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'social_share',
    label: 'AI 社交分享专家',
    role: '分享策略与素材管理专家',
    shortLabel: '享',
    icon: '🔗',
    color: '#00b42a',
    avatarUrl: '/avatars/avatar-social_share.png',
    category: 'ops_pilot',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=social_share',
    description: '分享策略管理、素材运营、埋点配置、AI内容生成',
    title: 'AI 社交分享专家',
    skillTags: [
      { icon: 'share-alt', text: '分享策略' },
      { icon: 'image', text: '素材运营' },
      { icon: 'code', text: '埋点配置' }
    ],
    sidebarGroup: 'content'
  },
  {
    id: 'strategy_group',
    label: 'AI 素材管理专家',
    role: '素材生成与审核自动化专家',
    shortLabel: '素',
    icon: '🖼️',
    color: '#9D5640',
    avatarUrl: '/avatars/avatar-strategy_group.png',
    category: 'strategy_group',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=strategy_group',
    description: '策略组素材生成、素材审核再生成、分享策略自动化、埋点关联',
    title: 'AI 素材管理专家',
    skillTags: [
      { icon: 'image', text: '素材生成' },
      { icon: 'edit', text: '审核再生成' },
      { icon: 'share-alt', text: '策略自动化' },
      { icon: 'code', text: '埋点关联' }
    ],
    sidebarGroup: 'content'
  },
  {
    id: 'legal',
    label: '狴犴',
    role: '法务合规专家',
    shortLabel: '法',
    icon: '⚖️',
    color: '#7c3aed',
    avatarUrl: '/avatars/avatar-legal.jpg',
    category: 'legal',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=legal',
    description: '防沉迷、未成年保护、服务条款、节假日与协议配置管理',
    title: '法务合规专家',
    skillTags: [
      { icon: 'safe', text: '防沉迷' },
      { icon: 'file-text', text: '服务条款' },
      { icon: 'user-group', text: '未成年保护' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'operation_resource_management',
    label: '维枢',
    role: '运维资源管理专家',
    shortLabel: '维',
    icon: '💳',
    color: '#0ea5e9',
    avatarUrl: '/avatars/avatar-channel_ops.png',
    category: 'ops_pilot',
    business: false, // 收敛为 channel_ops 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=operation_resource_management',
    description: '支付账号配置、商户号维护、渠道关联管理',
    title: '运维资源管理专家',
    skillTags: [
      { icon: 'credit-card', text: '支付配置' },
      { icon: 'settings', text: '商户维护' },
      { icon: 'link', text: '渠道关联' }
    ],
    sidebarGroup: 'business'
  },
  // ── 智能客服 ── L4: ReAct + RAG 知识库，已集成鹿鸣
  {
    id: 'customer_service',
    label: '鹿鸣',
    role: '智能客服专家',
    shortLabel: '客',
    icon: '🎧',
    color: '#14c9c9',
    avatarUrl: '/avatars/avatar-cs.png',
    category: 'customer_service',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=cs',
    description: '智能应答·情感识别·工单分流·产品反馈采集·企微沟通',
    title: '客服主管',
    skillTags: [
      { icon: 'heart', text: '情感抚慰' },
      { icon: 'message', text: '话术推荐' },
      { icon: 'list', text: '工单流转' },
      { icon: 'bulb', text: '产品反馈' },
      { icon: 'tags', text: '反馈分类' }
    ],
    sidebarGroup: 'customer'
  },
  // ── 社交服务 ── L3: ReAct + MCP，IMS/好友/LBS 运营配置
  {
    id: 'social',
    label: '云笺',
    role: '友乐社交管理专家',
    shortLabel: '友',
    icon: '💬',
    color: '#06b6d4',
    avatarUrl: '/avatars/avatar-social.png',
    category: 'ops_pilot',
    business: false, // 收敛为 social_media 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=social',
    description: 'IMS即时消息配置、社交好友关系设置、LBS地理位置管理',
    title: '好友管家',
    skillTags: [
      { icon: 'message', text: 'IMS配置' },
      { icon: 'user-group', text: '好友关系' },
      { icon: 'location', text: 'LBS管理' }
    ],
    sidebarGroup: 'customer'
  },
  // ── 社媒运营 ──
  {
    id: 'social_media',
    label: '听浪',
    role: '社媒运营专家',
    shortLabel: '媒',
    icon: '📱',
    color: '#eb2f96',
    avatarUrl: '/avatars/avatar-social_media.png',
    category: 'social_media',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=social_media',
    description: '社媒账号·舆情监控·IMS 配置·好友关系·LBS 管理',
    title: '社媒运营专家',
    skillTags: [
      { icon: 'compass', text: '舆情监控' },
      { icon: 'edit', text: '内容发布' },
      { icon: 'bar-chart', text: '社媒分析' },
      { icon: 'message', text: 'IMS 配置' },
      { icon: 'user-group', text: '好友关系' }
    ],
    sidebarGroup: 'content'
  },
  // ── 调研分析 ──
  {
    id: 'research',
    label: '听风',
    role: '调研分析专家',
    shortLabel: '调',
    icon: '🔍',
    color: '#722ed1',
    avatarUrl: '/avatars/avatar-research.png',
    category: 'research',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=research',
    description: '竞品调研、市场分析、行业研究',
    title: '市场总监',
    skillTags: [
      { icon: 'layers', text: '竞品拆解' },
      { icon: 'compass', text: '舆情监控' },
      { icon: 'file', text: '研报摘要' }
    ],
    sidebarGroup: 'business'
  },
  // ── 报告生成 ── 后端真实 Agent: apps/agent-service/src/agents/report/，
  // 由 intent_classifier OperationType.REPORT_GENERATE 路由触达。
  // plan-eng-review 2026-05-06 P2-3 补齐前后端 ID 一致性。
  {
    id: 'report',
    label: '编简',
    role: '分析报告专家',
    shortLabel: '报',
    icon: '📑',
    color: '#0ea5e9',
    avatarUrl: '/avatars/avatar-chatbi.png', // 报告生成收敛为 ChatBI 的 Skill，沿用 ChatBI 头像
    category: 'data_analyst',
    business: false, // 收敛为 chatbi 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L3',
    routePath: '/agents/workspace?agent=report',
    description: '基于 ChatBI 数据查询结果生成专业分析报告（趋势 / 归因 / 对比）',
    title: '数据报告专家',
    skillTags: [
      { icon: 'file', text: '报告生成' },
      { icon: 'bar-chart', text: '趋势分析' },
      { icon: 'edit', text: '排版优化' }
    ],
    sidebarGroup: 'business'
  },
  {
    id: 'product_feedback',
    label: '纳言',
    role: '产品反馈专家',
    shortLabel: '反',
    icon: '💡',
    color: '#8b5cf6',
    avatarUrl: '/avatars/avatar-product_feedback.png',
    category: 'research',
    business: false, // 收敛为 customer_service 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=product_feedback',
    description: '用户意见反馈采集、分类整理、采纳跟踪',
    title: '产品反馈专家',
    skillTags: [
      { icon: 'bulb', text: '反馈采集' },
      { icon: 'tags', text: '分类整理' },
      { icon: 'check-circle', text: '采纳跟踪' }
    ],
    sidebarGroup: 'customer'
  },
  // ── 营销 ──
  {
    id: 'user_recall',
    label: '归鸿',
    role: '用户召回专家',
    shortLabel: '召',
    icon: '🔄',
    color: '#6366f1',
    avatarUrl: '/avatars/avatar-user_recall.png',
    category: 'marketing',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=user_recall',
    description: '流失用户分群、策略匹配、多渠道自动触达',
    title: '用户召回专家',
    skillTags: [
      { icon: 'user-group', text: '用户分群' },
      { icon: 'thunderbolt', text: '策略匹配' },
      { icon: 'send', text: '多渠道触达' }
    ],
    sidebarGroup: 'customer'
  },
  // ── 内容生成 ──
  {
    id: 'creative',
    label: '流光',
    role: '内容创作专家',
    shortLabel: '创',
    icon: '🎨',
    color: '#a855f7',
    avatarUrl: '/avatars/avatar-creative.png',
    category: 'creative',
    business: true,
    enabled: true,
    maturityLevel: 'L4',
    routePath: '/agents/workspace?agent=creative',
    description: '生成创意内容、设计营销素材',
    title: '美术总监',
    skillTags: [
      { icon: 'pen', text: 'ICON设计' },
      { icon: 'video-camera', text: '视频生成' },
      { icon: 'edit', text: '营销文案' }
    ],
    sidebarGroup: 'content'
  },
  // ── 数据工程 ──
  {
    id: 'data_fabric',
    label: '织锦',
    role: '数据编织专家',
    shortLabel: '织',
    icon: '🔗',
    color: '#06b6d4',
    avatarUrl: '/avatars/avatar-data_fabric.png',
    category: 'data_analyst',
    business: false, // 收敛为 chatbi 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L2',
    routePath: '/agents/workspace?agent=data_fabric',
    description: '数据血缘探索、变更影响分析、资产搜索',
    title: '数据编织专家',
    skillTags: [
      { icon: 'branch', text: '数据血缘' },
      { icon: 'thunderbolt', text: '影响分析' },
      { icon: 'search', text: '资产搜索' }
    ],
    sidebarGroup: 'data'
  },
  {
    id: 'data_engineering',
    label: '铸基',
    role: '数据工程专家',
    shortLabel: '铸',
    icon: '⚙️',
    color: '#64748b',
    avatarUrl: '/avatars/avatar-data_engineering.png',
    category: 'data_analyst',
    business: false, // 收敛为 chatbi 的 Skill，不独立展示
    enabled: true,
    maturityLevel: 'L2',
    routePath: '/agents/workspace?agent=data_engineering',
    description: 'ETL 失败诊断、管道监控、集群资源分析',
    title: '数据工程专家',
    skillTags: [
      { icon: 'tool', text: 'ETL 诊断' },
      { icon: 'dashboard', text: '管道监控' },
      { icon: 'cloud-server', text: '集群分析' }
    ],
    sidebarGroup: 'data'
  },
  // ── Squad 复合 Agent（望舒智囊团）── composite 类型，不在蜂群面板独立展示
  {
    id: 'kpi_triage',
    label: '望舒智囊团',
    role: '增长作战室协作编队',
    shortLabel: '囊',
    icon: '🎯',
    color: '#4080FF',
    avatarUrl: '/avatars/avatar-strategy_group.png', // 协作编队沿用策略组头像（原文件不存在）
    category: 'composite',
    business: false,
    enabled: false,
    maturityLevel: 'L4',
    description: '数据分析 + 分享策略 + 运营推送三专家协作，共同解决增长问题'
  },
  // ── 以下为子技能/内部路由节点，不在蜂群面板展示 ──
  // ops (小运): 运营数据分析 | chart (小图): 图表生成
  // report (小报): 报告生成 | metric_query (指标通): 指标查询
  // ── 工具型（不显示在蜂群面板） ──
  {
    id: 'chat',
    label: '望舒',
    role: 'AI 数字员工总协调',
    icon: '🌙',
    color: '#6366f1',
    avatarUrl: '/avatars/avatar-auto.png',
    category: 'utility',
    business: false,
    enabled: false
  },
  {
    id: 'clarification',
    label: '洞明',
    role: '澄清助手',
    icon: '❓',
    color: '#86909c',
    avatarUrl: '/avatars/avatar-clarification.png',
    category: 'utility',
    business: false,
    enabled: false
  }
]

// ======================== Agent 欢迎页配置 ========================

/** Agent 聊天视图专属配置（avatarText + suggestedQueries） */
export interface AgentChatViewConfig {
  avatarText: string
  suggestedQueries: string[]
}

/**
 * Agent ID → 聊天视图配置
 *
 * 降级兜底：优先从 getChatConfig API 加载（DB 单一数据源），失败时使用此常量。
 * 修改时须与后端 DB agents.config.chat_config.suggest_questions 保持同步。
 *
 * 用于 GenericAgentView 数据驱动渲染，消除 11 个重复视图文件
 */
export const AGENT_CHAT_CONFIGS: Record<string, AgentChatViewConfig> = {
  ops_message: {
    avatarText: '消息',
    suggestedQueries: [
      '帮我写一条版本更新的推送通知',
      '本周推送打开率怎么样',
      '给付费用户做个站内信模板',
      '哪种消息类型转化最好'
    ]
  },
  announcement: {
    avatarText: '公告',
    suggestedQueries: [
      '帮我查看最近一周已发布的公告',
      '创建一个明天早上 10 点发布的版本更新公告',
      '把 ID 为 123 的公告标题改成春季版本更新',
      '帮我查一下当前有哪些临时维护公告还处于草稿状态'
    ]
  },
  legal: {
    avatarText: '法',
    suggestedQueries: [
      '查询当前实名配置',
      '修改未成年人文案配置',
      '查看当前服务条款配置',
      '按账号搜索用户'
    ]
  },
  operation_resource_management: {
    avatarText: '维',
    suggestedQueries: [
      '创建支付配置',
      '查询支付账号列表',
      '帮我配置一个新的商户号',
      '查看某个支付账号关联了哪些渠道'
    ]
  },
  channel_management: {
    avatarText: '渠',
    suggestedQueries: [
      '查询国内安卓主渠道列表',
      '帮我查看某个渠道的详细配置',
      '创建一个新的国内 iOS 主渠道',
      '校验这个渠道类型是否允许设置官包'
    ]
  },
  channel_ops: {
    avatarText: '渠道',
    suggestedQueries: [
      '各渠道这个月新增用户对比一下',
      '哪个渠道付费率最高',
      '看看应用宝最近表现怎么样',
      '下阶段渠道投放怎么排优先级'
    ]
  },
  anomaly_monitor: {
    avatarText: '监控',
    suggestedQueries: [
      '今天有没有数据异常',
      '帮我配个 DAU 下降 10% 的告警',
      '昨天收入突然掉了，帮我查查原因',
      '最近一周触发了哪些告警'
    ]
  },
  // 兼容历史：后端标准 ID 为 customer_service，这里同时保留 cs 映射，避免老会话配置丢失
  customer_service: {
    avatarText: '客服',
    suggestedQueries: [
      '今天工单情况怎么样',
      '常见问题解决率多少',
      '充值问题的回复模板优化一下',
      '最近玩家主要在投诉什么'
    ]
  },
  user_recall: {
    avatarText: '召回',
    suggestedQueries: [
      '最近一周流失了多少用户',
      '帮我设计个 7 日未登录的召回方案',
      '上次召回活动效果怎么样',
      '什么时间段发推送打开率最高'
    ]
  },
  product_feedback: {
    avatarText: '反馈',
    suggestedQueries: [
      '新建产品意见反馈',
      '获取意见反馈产品问题类型列表',
      '帮我整理一下待采纳的反馈',
      '下版本最该优先修什么'
    ]
  },
  creative: {
    avatarText: '创作',
    suggestedQueries: [
      '帮我写一段上线宣传文案',
      '来一组广告创意方案',
      '版本更新的推送文案帮我写下',
      '参考竞品风格出一批素材创意'
    ]
  },
  social_share: {
    avatarText: '分享',
    suggestedQueries: [
      '现在线上跑着哪些分享策略',
      '帮新用户做个首登分享引导',
      '最近一周各策略的分享率怎么样',
      '帮我写一套活动分享文案，配上图'
    ]
  },
  social: {
    avatarText: '社交',
    suggestedQueries: [
      '查看当前产品的 IMS 即时消息配置',
      '帮我修改 IMS 消息同步时长为 48 小时',
      '查看当前的社交好友关系配置',
      '查看所有 LBS 地理位置类型列表',
      '帮我查询某个用户的好友关系列表'
    ]
  },
  social_media: {
    avatarText: '听浪',
    suggestedQueries: [
      '最近玩家在聊什么，有没有负面舆情',
      '今天游戏圈有什么热点',
      '帮我整理近 3 天社媒负反馈',
      '我们和竞品的社媒热度对比一下'
    ]
  },
  research: {
    avatarText: '听风',
    suggestedQueries: [
      '帮我看看主要竞品最近在做什么',
      '今年手游市场有什么新趋势',
      '出一份竞品运营复盘报告',
      'SLG 品类国内外付费设计有什么不同'
    ]
  }
}

/**
 * 业务 Agent（排除闲聊、澄清等工具型 Agent）
 * @deprecated 运行时请使用 useAgentRegistryStore().businessAgents
 */
export const BUSINESS_AGENTS = REGISTERED_AGENTS.filter(a => a.business)

/**
 * Agent ID → 元数据 快速查找
 * @deprecated 运行时请使用 useAgentRegistryStore().agentById
 */
export const AGENT_BY_ID: Record<string, AgentRegistryItem> = Object.fromEntries(
  REGISTERED_AGENTS.map(a => [a.id, a])
)

/**
 * 所有已注册的 Agent ID 集合
 * @deprecated 运行时请使用 useAgentRegistryStore().agentIdSet
 */
export const AGENT_ID_SET = new Set(REGISTERED_AGENTS.map(a => a.id))

// ======================== 意图别名映射 ========================

/**
 * 意图/操作别名 → 标准 Agent ID
 *
 * 后端 router.py 中 _FORCED_AGENT_ALIASES 的前端镜像。
 * 历史数据和新数据中可能出现的各种别名都需要映射到标准 agent_id。
 */
export const INTENT_TO_AGENT_ID: Record<string, string> = {
  // ChatBI 数据分析（含子技能别名）
  smart_query: 'chatbi',
  data_query: 'chatbi',
  dashboard_builder: 'chatbi',
  dashboard_generation: 'chatbi',
  deep_insight: 'chatbi',
  export: 'chatbi',
  summary: 'chatbi',
  // 图表 & 报告 — 子技能统一归属到数策（chatbi），前端按 chatbi 展示
  chart_generate: 'chatbi',
  report_generate: 'chatbi',
  chart: 'chatbi',
  report: 'chatbi',
  metric_query: 'chatbi',
  // 创意（与后端 agent_registry._aliases 一致）
  content_creation: 'creative',
  copywriting: 'creative',
  material_gen: 'creative',
  // 其他 Agent 自映射（保证 onExecutionPlanCreated 能正确设置蜂群状态）
  announcement: 'announcement',
  customer_service: 'customer_service',
  ops_message: 'ops_message',
  legal: 'legal',
  // Skill 化收敛：子 Agent 意图映射到父 Agent（v1.0.6 后端已完成 P1+P2）
  operation_resource_management: 'channel_ops',       // 维枢 → 百川
  channel_management: 'channel_ops',                  // 渠析 → 百川
  // 青鸟子能力：邮件任务归属到 ops_message 主 Agent
  mail_task: 'ops_message',
  // 鸣镝 · 独立应用推送 Agent（已脱离青鸟）
  push_ops: 'push_ops',
  message_push: 'push_ops',  // BC：旧推送意图映射到独立的 push_ops
  product_feedback: 'customer_service',               // 纳言 → 鹿鸣
  social: 'social_media',                             // 云笺 → 听浪
  social_media: 'social_media',
  // 数据子 Agent 收敛到数策（P2 已完成）
  data_fabric: 'chatbi',                              // 织锦 → 数策
  data_engineering: 'chatbi',                         // 铸基 → 数策
  research: 'research',
  user_recall: 'user_recall',
  channel_ops: 'channel_ops',
  anomaly_monitor: 'anomaly_monitor',
  creative: 'creative',
  social_share: 'social_share',
  ops: 'chatbi',
  cs: 'customer_service'
}

/**
 * 根据意图/别名获取标准 Agent ID
 * 如果传入的已经是标准 agent_id 则直接返回
 */
export function resolveAgentId(intentOrAlias: string): string {
  return INTENT_TO_AGENT_ID[intentOrAlias] ?? intentOrAlias
}

// ======================== Agent Category 体系（元数据/模板） ========================

/** Agent 分类元数据（与 agent_templates.json 一致） */
export interface AgentCategoryMeta {
  key: string
  label: string
  icon: string
  color: string
}

/**
 * Agent 分类列表
 * 用于 Agent 目录筛选、能力配置分组
 */
export const AGENT_CATEGORIES: readonly AgentCategoryMeta[] = [
  { key: 'data_analyst', label: '数据分析', icon: '📊', color: '#3b82f6' },
  { key: 'ops_pilot', label: '游戏运营', icon: '🛠️', color: '#f59e0b' },
  { key: 'customer_service', label: '智能客服', icon: '💬', color: '#10b981' },
  { key: 'social_media', label: '社媒运营', icon: '📱', color: '#06b6d4' },
  { key: 'research', label: '调研分析', icon: '🔍', color: '#8b5cf6' },
  { key: 'marketing', label: '营销策略', icon: '📈', color: '#f97316' },
  { key: 'creative', label: '内容生成', icon: '🎨', color: '#ec4899' }
] as const

/** Category key → 元数据 快速查找 */
export const CATEGORY_BY_KEY: Record<string, AgentCategoryMeta> = Object.fromEntries(
  AGENT_CATEGORIES.map(c => [c.key, c])
)
